// File: src/pages/enquiry/EnquiryPage.js
import React, { useEffect, useState } from 'react';
import apiClient from '../../apiClient';
import useStatusOptions from '../../hooks/useStatusOptions';
import Header from '../../Component/common/Header/Header';
import SearchBar from '../../Component/common/SearchBar/SearchBar';
import AddEnquiryForm from '../../Component/BuyLeads/Enquiry/AddEnquiryForm';
import EditEnquiryPopup from '../../Component/BuyLeads/Enquiry/EditEnquiryPopup';
import SelectedPopup from '../../Component/common/selectedPopup';
import EnquiryTable from '../../Component/BuyLeads/Enquiry/EnquiryTable';

const EnquiryPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [allEnquiries, setAllEnquiries] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState('');

  const { statusOptions, loading: statusLoading, error: statusError } = useStatusOptions();

  useEffect(() => {
    fetchAllEnquiries();
  }, []);

  const fetchAllEnquiries = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/enquiries');
      const enquiriesData = response.data;
      setEnquiries(enquiriesData);
      setAllEnquiries(enquiriesData);
      setFilteredEnquiries(enquiriesData);
      // setMessage(enquiriesData.length > 0 
      //   ? `Fetched ${enquiriesData.length} enquiries` 
      //   : 'No enquiries found');
    } catch (error) {
      console.error('Error fetching enquiries:', error.response?.data || error.message);
      setMessage('Failed to fetch enquiries. Please check console for details.');
      setError('Failed to fetch enquiries');
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const enquiryToEdit = enquiries.find(enquiry => enquiry.id === id);
      if (!enquiryToEdit) {
        alert('Enquiry not found');
        return;
      }
      const response = await apiClient.get(`/api/enquiries/${enquiryToEdit.enquiryID}`);
      const enquiryData = response.data;
      const enrichedEnquiry = {
        ...enquiryData.enquiries,
        EnquiryAttributes: (enquiryData.attributes || []).map(attr => ({
          id: attr.id,
          attributeName: attr.attributeName || attr.name,
          attributeValue: attr.attributeValue || attr.value
        })),
        sellers: enquiryData.sellers,
        VCsellers: enquiryData.VCsellers,
      };
      setEditingEnquiry(enrichedEnquiry);
    } catch (error) {
      console.error('Failed to fetch enquiry details for edit:', error);
      alert('Failed to fetch enquiry details for editing');
    }
  };

  const handleSave = async (updatedEnquiry) => {
    try {
      const { id, enquiryID, attachment, removeAttachment, ...updateData } = updatedEnquiry;
      
      const formData = new FormData();
      
      Object.keys(updateData).forEach(key => {
        if (key === 'EnquiryAttributes') {
          const sanitizedAttrs = updateData[key].map(attr => ({
            id: attr.id,
            name: String(attr.attributeName || attr.name),
            value: String(attr.attributeValue || attr.value)
          }));
          formData.append('attributes', JSON.stringify(sanitizedAttrs));
        } else {
          formData.append(key, updateData[key]);
        }
      });
      
      if (attachment) {
        formData.append('attachment', attachment);
      }
      
      if (removeAttachment) {
        formData.append('removeAttachment', 'true');
      }
      
      await apiClient.put(`/api/enquiries/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const response = await apiClient.get(`/api/enquiries/${enquiryID}`);
      const combinedData = {
        ...response.data.enquiries,
        attributes: response.data.attributes,
        sellers: response.data.sellers,
        VCsellers: response.data.VCsellers,
      };
      
      setEnquiries(enquiries.map(enquiry => 
        enquiry.id === id ? combinedData : enquiry
      ));
      
      setSelectedEnquiry(combinedData);
      setEditingEnquiry(null);
      setSelectedEnquiry(combinedData);
      
      alert('Enquiry updated successfully!');
      await fetchAllEnquiries();
    } catch (err) {
      console.error('Error updating enquiry:', err);
      alert('Failed to update enquiry');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      try {
        await apiClient.delete('/api/enquiries/' + id);
        setEnquiries(enquiries.filter(enquiry => enquiry.id !== id));
        setAllEnquiries(allEnquiries.filter(enquiry => enquiry.id !== id));
        setFilteredEnquiries(filteredEnquiries.filter(enquiry => enquiry.id !== id));
        alert('Enquiry deleted successfully!');
      } catch (err) {
        console.error('Error deleting enquiry:', err);
        alert('Failed to delete enquiry');
      }
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = allEnquiries.filter((enquiry) =>
      ["enquiryID", "buyerName", "emailID", "phoneNumber", "description", "productType"].some((key) =>
        enquiry[key]?.toString().toLowerCase().includes(query)
      )
    );
    setFilteredEnquiries(filtered);
  };

  const handleRowClick = async (enquiry) => {
    try {
      const response = await apiClient.get(`/api/enquiries/${enquiry.enquiryID}`);
      const combinedData = {
        ...response.data.enquiries,
        attributes: response.data.attributes,
        EnquiryAttributes: response.data.attributes,
        sellers: response.data.sellers,
        VCsellers: response.data.VCsellers,
      };
      setSelectedEnquiry(combinedData);
    } catch (err) {
      console.error('Error fetching enquiry details:', err);
      alert('Failed to fetch enquiry details');
    }
  };

  const closePopup = () => {
    setSelectedEnquiry(null);
  };

  const closeEditPopup = () => {
    setEditingEnquiry(null);
  };

  const handleToggleAddForm = () => {
    setShowAddForm(!showAddForm);
  };

  const handleAddNewEnquiry = async (newEnquiryData) => {
    try {
      const formData = new FormData();
      Object.keys(newEnquiryData).forEach(key => {
        if (key === "attachment" && newEnquiryData[key] instanceof File) {
          formData.append(key, newEnquiryData[key]);
        } else if (key === "attributes") {
          formData.append(key, JSON.stringify(newEnquiryData[key]));
        } else {
          formData.append(key, newEnquiryData[key]);
        }
      });

      await apiClient.post('/api/enquiries', formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Enquiry added successfully!");
      setShowAddForm(false);
      await fetchAllEnquiries();
    } catch (error) {
      console.error('Error adding enquiry:', error);
      alert("Error adding enquiry. Please try again.");
    }
  };

  const newEnquiryFormData = {
    enquiryID: "",
    date: new Date().toISOString().split('T')[0],
    buyerName: "",
    description: "",
    quotationFor: "",
    productType: "",
    emailID: "",
    phoneNumber: "",
    city: "",
    state: "",
    status: "Pending",
    cat_Id: "",
    note: "",
    attributes: [{ name: "", value: "" }],
    attachment: null,
  };

  // EnquiryTable component is now imported from external file

  if (loading) return <div className="loading-container">Loading enquiries...</div>;
  if (error) return <div className="error-container">{error}</div>;

  return (
    <div className="enquiry-page-container">
      <div className="filters-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Header
          title="All Enquiries"
          onAddAction={() => setShowAddForm(true)}
        />
        <SearchBar
          placeholder="Search by ID, Name, Email, Phone, Description, or Product Type"
          value={searchQuery}
          onChange={handleSearchInputChange}
          className="enquiry-search-bar"
          showIcon={true}
        />
        <button className="btn add-btn" onClick={handleToggleAddForm}>
          Create New
        </button>
      </div>

      {message && (
        <div className="message-container">
          {message}
        </div>
      )}

      <EnquiryTable 
        enquiries={searchQuery ? filteredEnquiries : enquiries}
        allEnquiries={allEnquiries}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRowClick={handleRowClick}
        loading={loading}
      />
      
      {selectedEnquiry && (
        <SelectedPopup 
          selectedEnquiry={selectedEnquiry}
          closePopup={closePopup}
          itemType="enquiry"
          onEdit={handleEdit}
        />
      )}
      
      {editingEnquiry && (
        <EditEnquiryPopup
          enquiry={editingEnquiry} 
          onSave={handleSave} 
          onClose={closeEditPopup}
          statusOptions={statusOptions}
        />
      )}
      
      {showAddForm && (
        <AddEnquiryForm
          showAddForm={showAddForm}
          handleToggleAddForm={handleToggleAddForm}
          closePopup={handleToggleAddForm}
          isLoading={loading}
          handleAddNewEnquiry={handleAddNewEnquiry}
          newEnquiryFormData={newEnquiryFormData}
          handleNewEnquiryChange={() => {}}
          statusOptions={statusOptions}
        />
      )}
    </div>
  );
};

export default EnquiryPage;
